import type { Kmat } from "~/Configurators/Linear/models";
import { isNonNull } from "~/utils/reducers";

interface Condition {
  check: (state: Kmat[]) => boolean;
}

type LCContext = { mainKmat: Kmat; subKmats: Kmat[] };
type LCCalculatedValue<T> = (context: LCContext) => T;
type LCPredicate = LCCalculatedValue<boolean>;
type Resolvable<T> = { condition: LCPredicate; value: T | LCCalculatedValue<T> } | T;

export type DynamicValue<T> = T | Resolvable<T>[];

function canBeResolved<T>(x: Resolvable<T>): x is { condition: LCPredicate; value: T | LCCalculatedValue<T> } {
  return typeof x === "object" && !!x && "condition" in x;
}

type LCTile = {
  name: DynamicValue<string>;
  description: DynamicValue<string>;
  cstics: DynamicValue<{ csticName: string; value: string }>[];
};

export class LCValueResolver {
  constructor(private context: LCContext) {}

  resolveNullableValue<T>(value: DynamicValue<T>): T | null {
    if (!Array.isArray(value)) {
      return value;
    }

    const val = value.find((item) => (canBeResolved(item) ? item.condition(this.context) : true));

    if (val === undefined) {
      return null;
    }
    if (!canBeResolved(val)) {
      return val;
    }

    if (typeof val.value === "function") {
      // @ts-ignore
      return val.value(this.context);
    }
    return val.value;
  }

  resolveDefaultValue<T>(dynamicValue: SerializableDynamicValue<T>): T | null {
    if (!Array.isArray(dynamicValue)) {
      return dynamicValue;
    }
    for (const item of dynamicValue) {
      if (typeof item !== "object" || item === null) {
        return item;
      }
      if (!("condition" in item)) {
        return item as T | null;
      }
      const conditionalItem = item as SerializableResolvableValue<T>;
      if (this.validateCondition(conditionalItem.condition)) {
        return conditionalItem.value;
      }
    }
    return null;
  }

  validateCondition(condition: SerializablePredicate): boolean {
    if (condition.type === "and") {
      return condition.value.every((item) => this.validateCondition(item));
    }
    if (condition.type === "or") {
      return condition.value.some((item) => this.validateCondition(item));
    }
    if (condition.type === "not") {
      return !this.validateCondition(condition.value);
    }
    if (condition.type === "equals") {
      const validValues = Array.isArray(condition.value) ? condition.value : [condition.value];
      const value = this.getCsticValue(condition.cstic);
      return validValues.includes(value);
    }
    if (condition.type === "valueAvailable") {
      const possibleValues = this.getCsticPossibleValues(condition.cstic);
      return possibleValues.includes(condition.value);
    }
    throw new Error("Unknown condition type.");
  }

  private getCsticValue(cstic: string): string {
    return this.context.mainKmat.cstics[cstic]?.value ?? "";
  }

  private getCsticPossibleValues(cstic: string): string[] {
    return this.context.mainKmat.cstics[cstic]?.values.map((v) => v.value) ?? [];
  }

  resolveValue<T>(value: DynamicValue<T>): T {
    const val = this.resolveNullableValue(value);
    if (val === null) {
      throw new Error("Could not resolve value");
    }
    return val;
  }

  resolveList<T>(value: DynamicValue<T>[]): T[] {
    return value.map((item) => this.resolveNullableValue(item)).filter(isNonNull);
  }
}

export type SerializableDynamicValue<T> =
  | T
  | SerializableResolvableValue<T>[]
  | [...SerializableResolvableValue<T>[], T];
export type SerializableResolvableValue<T> = { condition: SerializablePredicate; value: T };

export type AndPredicate = {
  type: "and";
  value: SerializablePredicate[];
};
export type OrPredicate = {
  type: "or";
  value: SerializablePredicate[];
};
export type NotPredicate = {
  type: "not";
  value: SerializablePredicate;
};
export type CsticEqualsPredicate = {
  type: "equals";
  cstic: string;
  value: string | string[];
};
export type CsticValueAvailablePredicate = {
  type: "valueAvailable";
  cstic: string;
  value: string;
};

export type SerializablePredicate =
  | AndPredicate
  | OrPredicate
  | NotPredicate
  | CsticEqualsPredicate
  | CsticValueAvailablePredicate;
